"use client"

/**
 * Notification Item Component
 * Individual notification display
 */

import React from "react";
import { formatDistanceToNow } from "date-fns";
import { useNotificationActions } from "@/hooks/useNotifications";
import type { Notification, NotificationCategory } from "@/types/notification";
import {
    Trophy,
    BookOpen,
    Bell,
    Users,
    CreditCard,
    Clock,
    GraduationCap,
    CheckCircle2,
    Info,
    ChevronRight,
    Circle
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface NotificationItemProps {
    notification: Notification;
    onClick?: () => void;
}

const categoryConfig: Record<NotificationCategory, { icon: React.ReactNode, colorClass: string, bgClass: string, badgeVariant: "default" | "secondary" | "destructive" | "outline" }> = {
    SYSTEM: {
        icon: <Bell size={14} />,
        colorClass: "text-slate-600 dark:text-slate-300",
        bgClass: "bg-slate-100 dark:bg-slate-800",
        badgeVariant: "secondary"
    },
    ACHIEVEMENT: {
        icon: <Trophy size={14} />,
        colorClass: "text-amber-600 dark:text-amber-400",
        bgClass: "bg-amber-100 dark:bg-amber-900/40",
        badgeVariant: "outline"
    },
    QUIZ: {
        icon: <CheckCircle2 size={14} />,
        colorClass: "text-emerald-600 dark:text-emerald-400",
        bgClass: "bg-emerald-100 dark:bg-emerald-900/40",
        badgeVariant: "outline"
    },
    PRACTICE: {
        icon: <BookOpen size={14} />,
        colorClass: "text-blue-600 dark:text-blue-400",
        bgClass: "bg-blue-100 dark:bg-blue-900/40",
        badgeVariant: "outline"
    },
    COURSE: {
        icon: <GraduationCap size={14} />,
        colorClass: "text-violet-600 dark:text-violet-400",
        bgClass: "bg-violet-100 dark:bg-violet-900/40",
        badgeVariant: "outline"
    },
    SOCIAL: {
        icon: <Users size={14} />,
        colorClass: "text-pink-600 dark:text-pink-400",
        bgClass: "bg-pink-100 dark:bg-pink-900/40",
        badgeVariant: "outline"
    },
    PAYMENT: {
        icon: <CreditCard size={14} />,
        colorClass: "text-teal-600 dark:text-teal-400",
        bgClass: "bg-teal-100 dark:bg-teal-900/40",
        badgeVariant: "outline"
    },
    REMINDER: {
        icon: <Clock size={14} />,
        colorClass: "text-orange-600 dark:text-orange-400",
        bgClass: "bg-orange-100 dark:bg-orange-900/40",
        badgeVariant: "outline"
    },
};

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClick }) => {
    const { markAsRead, markAsClicked } = useNotificationActions();

    const handleClick = async () => {
        await markAsClicked(notification.id);
        if (notification.action_url) {
            window.location.href = notification.action_url;
        }
        onClick?.();
    };

    const handleMarkAsRead = async (e: React.MouseEvent) => {
        e.stopPropagation();
        await markAsRead(notification.id);
    };

    const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
        addSuffix: true,
    });

    const config = categoryConfig[notification.category] || categoryConfig.SYSTEM;

    return (
        <div
            className={cn(
                "group relative flex items-start gap-4 p-4 cursor-pointer transition-all duration-200 border-b border-border/40 last:border-0 hover:bg-muted/40",
                !notification.is_read ? "bg-primary/[0.03]" : "bg-transparent",
            )}
            onClick={handleClick}
        >
            {/* Unread Indicator */}
            {!notification.is_read && (
                <div className="absolute left-2 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-primary/20 hidden sm:block" >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                </div>
            )}

            {/* Icon */}
            <div className={cn(
                "shrink-0 w-9 h-9 flex items-center justify-center rounded-lg shadow-sm border border-black/5 dark:border-white/5 mt-0.5",
                config.bgClass,
                config.colorClass
            )}>
                {config.icon}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-start justify-between gap-2">
                    <h4 className={cn(
                        "text-sm font-semibold leading-tight tracking-tight",
                        !notification.is_read ? 'text-foreground' : 'text-foreground/80'
                    )}>
                        {notification.title}
                    </h4>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap font-medium shrink-0 pt-0.5">
                        {timeAgo}
                    </span>
                </div>

                <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                    {notification.message}
                </p>

                {/* Badges/Tags */}
                <div className="flex items-center gap-2 mt-2">
                    {/* Category Label - minimalist */}
                    <span className={cn(
                        "text-[10px] uppercase tracking-wider font-bold",
                        config.colorClass
                    )}>
                        {notification.category}
                    </span>

                    {notification.priority === "URGENT" && (
                        <Badge variant="destructive" className="text-[10px] px-1.5 h-4 font-bold uppercase tracking-wider">
                            URGENT
                        </Badge>
                    )}
                </div>
            </div>

            {/* Hover Actions */}
            <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center gap-1">
                {!notification.is_read && (
                    <button
                        className="h-7 w-7 rounded-full flex items-center justify-center bg-background border shadow-sm text-muted-foreground hover:text-primary hover:border-primary transition-all duration-200"
                        onClick={handleMarkAsRead}
                        title="Mark as read"
                    >
                        <Circle size={8} fill="currentColor" className="text-primary" />
                    </button>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground/30" />
            </div>

            {/* Mobile Unread Dot */}
            {!notification.is_read && (
                <div className="absolute right-3 top-3 w-2 h-2 rounded-full bg-primary sm:hidden" />
            )}

        </div>
    );
};

export default NotificationItem;
